import React from "react"
import PropTypes from "prop-types"
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'

import Stack from "@UILibrary/Stack"
import { Modal, ModalSubHeader, ModalHeader, ModalContent } from "@components/Modal/index.jsx"
import { Button } from '@components/Button'
import SectionSelect from '@components/Form/SectionSelect'
import { TrackingEvents } from '@utils/tracking'
import useHTTPRequest from '@hooks/useHTTPRequest'

import {
  AstuteCCPDeclarationDetails,
  AstuteCCPProceedDisclaimer
} from "./AstuteRequestConfirmProceedModal.AstuteCCPDeclaration.jsx"

const ASTUTE_PRODUCT_SETS = [
  { label: "LifeAndRisk Products", name: "LifeAndRisk" },
  { label: "UnitTrust Products", name: "UnitTrust" },
  { label: "LinkedInvestment Products (funds and other investments)", name: "LinkedInvestment" }
]
const DEFAULT_FORM_VALUES = ASTUTE_PRODUCT_SETS.reduce((acc, item) => ({
  ...acc,
  [item.name]: true
}), {})

const AstuteRequestConfirmProceedModal = ({ show, onClose, createAstuteRequestPath }) => {
  return (
    <Modal
      open={show}
      onClose={onClose}>
      <ModalHeader>Get an Astute Report</ModalHeader>
      <ModalSubHeader>Configure your report and accept the declaration</ModalSubHeader>
      <ModalContent>
        <AstuteRequestConfirmProceedModalForm
          onClose={onClose}
          url={createAstuteRequestPath} />
      </ModalContent>
    </Modal>
  )
}

const validationSchema = Yup.object().test('atLeastOneChecked', null, (values) => {
  const { LifeAndRisk, UnitTrust, LinkedInvestment } = values;
  if (!LifeAndRisk && !UnitTrust && !LinkedInvestment) {
    throw new Yup.ValidationError('Please select at least one option', null, 'atLeastOneChecked');
  }
  return true;
});

const AstuteRequestConfirmProceedModalForm = ({ onClose, url }) => {
  const request = useHTTPRequest({ url, method: "POST", followRedirects: true })
  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)
    const payload = {
      astute_request: {
        product_sets: Object.keys(values).filter(key => values[key])
      }
    }
    await request.execute(payload)
  }

  return (
    <>
      <p className="text-center">
        You can view your Astute reports requested from Wealthbit at anytime, import assets and risk products from an Astute report into your client’s profile, and download a PDF version of the report to send to your client.
      </p>

      <Formik
        initialValues={DEFAULT_FORM_VALUES}
        validationSchema={validationSchema}
        onSubmit={onSubmit}>
        {(props) => (
          <FormikForm className='app-form'>
            <Stack vertical spacing="m">
              <Stack vertical spacing="xxs">
                <SectionSelect sections={[{
                  title: "Astute products",
                  description: "Select the product types to include in the request",
                  items: ASTUTE_PRODUCT_SETS
                }]}/>
                <AtLeastOneSelectedErrorMessage values={props.values} />
              </Stack>
              <Stack horizontal spacing="m" justifyContent="center">
                <Button
                  secondary
                  onClick={onClose}
                  disabled={buttonDisabled(props.isSubmitting, props.values)}>
                  Cancel
                </Button>
                <Button
                  primary
                  disabled={buttonDisabled(props.isSubmitting, props.values)}
                  trackAs={TrackingEvents.implementedPlan.getAstuteReport}
                  type='submit'>
                  Accept & Continue
                </Button>
              </Stack>
            </Stack>
          </FormikForm>
        )}
      </Formik>

      <Stack
        vertical
        spacing="m"
        style={{marginBlockStart: '.825rem'}}>
        <AstuteCCPProceedDisclaimer style={{textAlign: 'center'}}/>
        <AstuteCCPDeclarationDetails />
      </Stack>
    </>
  )
}

const buttonDisabled = (isSubmitting, values) => {
  const { LifeAndRisk, UnitTrust, LinkedInvestment } = values
  const atLeastOneSelected = LifeAndRisk || UnitTrust || LinkedInvestment
  return isSubmitting || !atLeastOneSelected
}

const AtLeastOneSelectedErrorMessage = ({ values }) => {
  const { LifeAndRisk, UnitTrust, LinkedInvestment } = values
  const atLeastOneSelected = LifeAndRisk || UnitTrust || LinkedInvestment

  return (
    <span
      style={{
        visibility: (atLeastOneSelected) ? "hidden" : "visible",
        color: "red",
        display: "block",
        textAlign: "center",
      }}>
      Please select at least one option
    </span>
  )
}

AstuteRequestConfirmProceedModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  createAstuteRequestPath: PropTypes.string.isRequired
}

export default AstuteRequestConfirmProceedModal
